import React, { lazy, Suspense } from "react";

import profile1 from "@/assets/waggex/waggex-profile-settings-company-control-dashboard.webp?w=400&format=webp";
// import GeoLocalizationMobile from "@/assets/GeoLocalization/waggex-gps-employee-tracking-app-india-mobile.webp";

const CustomerReviews = lazy(
  () => import("../components/payrolewebapp/CustomerReviews"),
);

const ComparisonTable = lazy(
  () => import("../components/payrolewebapp/ComparisonTable"),
);

const HomeFaq = lazy(() => import("./HomeFaq"));

const AppDownloadCard = lazy(
  () => import("../components/common/AppDownloadCard"),
);

const PayrollBanner = lazy(() => import("../components/common/PayrollBanner"));

const ModernizeHR = lazy(() => import("../components/common/ModernizeHR"));
const DynamicFeaturesSection = lazy(
  () => import("../components/common/DynamicFeaturesSection"),
);

import {
  ShieldCheck,
  Users,
  Check,
  UserCog,
  Palette,
  BadgePercent,
  LayoutDashboard,
  FileBadge,
  FolderKanban,
  TrendingUp,
} from "lucide-react";

import StatsSection from "../components/common/StatsSection";
import { Helmet } from "react-helmet-async";
import { useHrmsCounts } from "../hooks/useHrmsCounts";
import { useInView } from "react-intersection-observer";

const referralFeatures = [
  "Referral code access",
  "Referral history",
  "Referral tracking",
  "Referral benefit visibility",
  "Company referral management",
];
const Faqs1 = [
  {
    title: "What can I manage in Waggex Profile Settings?",
    content:
      "Profile Settings is the central place to manage company information, branding, user permissions, document templates, referral settings, and other organization-wide preferences. It helps administrators keep essential business settings organized from one dashboard.",
  },
  {
    title: "Can I customize HR documents with my company branding?",
    content:
      "Yes. You can add your company logo, configure brand colors, and customize document templates so that payslips, offer letters, appointment letters, experience letters, and other HR documents maintain a consistent professional appearance.",
  },
  {
    title: "How does user management work in Waggex?",
    content:
      "Administrators can add new users, assign role-based permissions, and control access to different HRMS modules. This helps protect sensitive employee and payroll information while giving each user access only to the features they need.",
  },
];

const Faqs2 = [
  {
    title: "What is the purpose of the Referral System?",
    content:
      "The Referral System allows businesses to manage referral settings, review referral activity, and monitor referral-related records from one centralized location within the platform.",
  },
  {
    title: "Who can access Profile Settings?",
    content:
      "Profile Settings is available only to authorized users. Company administrators can assign role-based permissions, ensuring only approved users can update company settings, branding, and other administrative configurations.",
  },
  {
    title: "Do Profile Settings integrate with other Waggex HRMS modules?",
    content:
      "Yes. Profile Settings integrates seamlessly with Payroll, Attendance, Leave, Shift Management, Employee Management, Task Management, Document Management, and other Waggex HRMS modules, ensuring your company settings are applied consistently across the platform.",
  },
];

const adminFeatures = [
  {
    icon: BadgePercent,
    title: "Referral System",
    description:
      "Access and manage employee referral programs, track referrals, and configure referral-related settings from the company portal.",
  },
  {
    icon: UserCog,
    title: "Manage Users",
    description:
      "Add, edit, remove, and manage employee accounts, roles, and permissions within the company portal.",
  },
  {
    icon: Palette,
    title: "Branding Settings",
    description:
      "Customize company branding, document templates, PDF colors, logos, and themes for HR letters and payslips.",
  },
];
const brandingFeatures = [
  {
    id: 1,
    title: "Document PDF Templates",
    desc: "Select professional templates for HR and payroll documents.",
  },
  {
    id: 2,
    title: "Offer Letter Templates",
    desc: "Choose branded offer letter layouts for new employees.",
  },
  {
    id: 3,
    title: "Experience Letter Templates",
    desc: "Generate professional experience letters using selected document templates.",
  },
  {
    id: 4,
    title: "Payslip Templates",
    desc: "Create clean and branded payslips for salary processing.",
  },
  {
    id: 5,
    title: "Primary Template Color",
    desc: "Set the main color used in the selected PDF document template.",
  },
  {
    id: 6,
    title: "Secondary Template Color",
    desc: "Choose a secondary color to improve the overall document appearance.",
  },
  {
    id: 7,
    title: "Live Letter Preview",
    desc: "Preview the selected template and colors before saving the branding settings.",
  },
];

const userfeatures = [
  "Add company users",
  "Update user details",
  "Manage access control",
  "Control internal portal usage",
  "Support role-based access",
  "Improve company account security",
];

const profileSettingBenefits = [
  {
    id: 1,
    icon: LayoutDashboard,
    title: "Centralised Company Settings",
    desc: "Manage branding, users, referral preferences, document templates, and other company settings from one organized dashboard instead of multiple modules.",
  },
  {
    id: 2,
    icon: ShieldCheck,
    title: "Active User Control",
    desc: "Assign permissions based on employee roles, giving HR teams, managers, and administrators secure access to only the features they need.",
  },
  {
    id: 3,
    icon: FileBadge,
    title: "Professional Document Branding",
    desc: "Customize logos, brand colors, and document templates to create professional HR letters, payslips, and employee documents with a consistent identity.",
  },
  {
    id: 4,
    icon: FolderKanban,
    title: "Organised Admin Experience",
    desc: "Keep company configurations organized in one place, making it easier to manage settings without interrupting everyday HR and payroll operations.",
  },
  {
    id: 5,
    icon: UserCog,
    title: "Improved Management",
    desc: "Easily add users, update permissions, and manage administrator access while maintaining better control over company information and sensitive data.",
  },
  {
    id: 6,
    icon: TrendingUp,
    title: "Useful for Growing Businesses",
    desc: "As your organization grows, update users, branding, referrals, and company preferences from one platform without adding administrative complexity.",
  },
];
const steps = [
  {
    id: 1,
    step: "Step 1",
    title: "Company Sign Up",
    desc: "Company administrator creates a Waggex account and logs in to access the company portal and Profile Settings.",
  },
  {
    id: 2,
    step: "Step 2",
    title: "Complete Company Setup",
    desc: "Enter company information, configure business details, upload branding assets, and complete the initial HR setup.",
  },
  {
    id: 3,
    step: "Step 3",
    title: "Configure Company Preferences",
    desc: "Set working days, attendance rules, document branding, referral settings, and user management preferences.",
  },
  {
    id: 4,
    step: "Step 4",
    title: "Manage Users & Permissions",
    desc: "Add company users, assign role-based permissions, and control access to HR, payroll, attendance, and other modules.",
  },
  {
    id: 5,
    step: "Step 5",
    title: "Customize Profile Settings",
    desc: "Manage referral options, company branding, document templates, notifications, and other account preferences from one place.",
  },
  {
    id: 6,
    step: "Step 6",
    title: "Save & Apply Settings",
    desc: "Save all configured settings to instantly apply them across your company portal and employee management system.",
  },
];

const hrmsModules = [
  "Payroll Management",
  "Attendance Management",
  "Leave Management",
  "Shift Management",
  "Task Management",
  "Employee Management",
  "Reminder Management",
  "Tax & Form Management",
  "Reports & Analysis",
];

const schema = [
  {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Waggex Profile Settings",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, Android, iOS",
    description:
      "Waggex Profile Settings allows administrators to manage company users, customize HR document branding with PDF templates and brand colors, configure referral settings, and control organization-wide preferences from one centralized dashboard.",
    url: "https://www.waggex.com/profile-settings",
    featureList: [
      "Company profile settings HRMS",
      "HR document branding software India",
      "Manage company users HRMS",
      "Payroll document template software",
      "Referral system HRMS India",
    ],
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "INR",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "167",
      bestRating: "5",
      worstRating: "1",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What can I manage in Waggex Profile Settings?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Profile Settings is the central place to manage company information, branding, user permissions, document templates, referral settings, and other organization-wide preferences. It helps administrators keep essential business settings organized from one dashboard.",
        },
      },
      {
        "@type": "Question",
        name: "Can I customize HR documents with my company branding?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. You can add your company logo, configure brand colors, and customize document templates so that payslips, offer letters, appointment letters, experience letters, and other HR documents maintain a consistent professional appearance.",
        },
      },
      {
        "@type": "Question",
        name: "How does user management work in Waggex?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Administrators can add new users, assign role-based permissions, and control access to different HRMS modules. This helps protect sensitive employee and payroll information while giving each user access only to the features they need.",
        },
      },
      {
        "@type": "Question",
        name: "What is the purpose of the Referral System?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The Referral System allows businesses to manage referral settings, review referral activity, and monitor referral-related records from one centralized location within the platform.",
        },
      },
      {
        "@type": "Question",
        name: "Who can access Profile Settings?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Profile Settings is available only to authorized users. Company administrators can assign role-based permissions, ensuring only approved users can update company settings, branding, and other administrative configurations.",
        },
      },
      {
        "@type": "Question",
        name: "Do Profile Settings integrate with other Waggex HRMS modules?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Profile Settings integrates seamlessly with Payroll, Attendance, Leave, Shift Management, Employee Management, Task Management, Document Management, and other Waggex HRMS modules, ensuring company settings are applied consistently across the platform.",
        },
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Waggex",
    url: "https://www.waggex.com/",
    logo: "https://www.waggex.com/waggex-payroll-software-logo.png",
    description:
      "Waggex provides HRMS and payroll software for Indian businesses including profile settings, employee management, attendance management, payroll processing, referral management, and document branding.",
    areaServed: "IN",
  },
];

export default function ProfileSettings() {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { counts, loading } = useHrmsCounts(inView);
  return (
    <>
      <Helmet>
        <title>Company Profile Settings & User Management Softwar</title>
        <meta
          name="description"
          content="Manage company profile settings, user roles, branding, HR templates, referral options, and admin controls from one centralized Waggex dashboard"
        />
        <meta
          name="keywords"
          content="company profile settings software,HR document branding software India,HRMS company settings management,payroll document template software India,manage company users HRMS,referral system HRMS India,HR branding settings software,company portal settings India,Waggex profile settings
"
        />
        <link rel="canonical" href="https://www.waggex.com/profile-settings" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section className="w-full bg-[#d1dfea] overflow-hidden py-10 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-10">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Heading */}
              <h1 className="text-[#10324F] font-bold uppercase tracking-wide text-xl md:text-[28px]">
                Manage Your Company <br />
                Settings in One Place
              </h1>

              {/* Description */}
              <p className="service-list-item-text py-4 max-w-md sm:max-w-2xl mx-auto">
                Control your company's HRMS experience from one place. Manage
                business information, branding, user access, document templates,
                referral settings, and administrative preferences through a
                centralized dashboard built for efficient day-to-day management
              </p>

              {/* Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button
                  onClick={() =>
                    (window.location.href = "https://app.waggex.com/signup")
                  }
                  className="w-full sm:w-auto bg-[#10324F] hover:bg-[#10324F] transition-all duration-300 text-white px-14 py-3 rounded-xl shadow-md text-sm sm:text-base font-medium"
                >
                  Start Free Today
                </button>

                <button
                  onClick={() => (window.location.href = "/contact-us")}
                  className="w-full sm:w-auto border border-[#10324F] text-[#10324F] hover:bg-[#10324F] hover:text-white transition-all duration-300 px-14 py-3 rounded-xl text-sm sm:text-base font-medium"
                >
                  Book a Demo
                </button>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative flex justify-center items-center">
              {/* Phones Image */}
              <picture>
                <source media="(max-width: 639px)" srcSet={profile1} />

                <img
                  src={profile1}
                  width={400}
                  height={377}
                  fetchPriority="high"
                  loading="eager"
                  decoding="async"
                  alt="Waggex profile settings for complete company control showing branding, manage user, referral system and document template options"
                  className="object-contain"
                />
              </picture>
            </div>
          </div>
        </div>
      </section>
      <section ref={ref}>
        <StatsSection counts={counts} loading={loading} />
      </section>
      <section className="w-full bg-white px-6 py-10">
        <div className="max-w-6xl mx-auto mt-10 mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-6 text-center lg:text-left">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-roboto font-bold leading-snug">
                What Is Profile Settings in Waggex?
              </h2>

              <p className="selfie-service-list-item-text py-4">
                Profile Settings is your company's central administration hub in
                Waggex. Manage business information, user access, branding
                preferences, document templates, referral settings, and other
                organization-wide configurations from one place instead of
                navigating multiple modules
              </p>

              <p className="selfie-service-list-item-text">
                Configure your HRMS to match your company's requirements while
                keeping settings organized, secure, and easy to manage for
                administrators
              </p>
            </div>

            {/* Right Cards */}
            <div className="space-y-5">
              {adminFeatures.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    style={{
                      boxShadow: `
                      rgb(167 191 210 / 40%) -15px -15px 20px inset,
                      rgb(170 188 202 / 30%) -20px -20px 30px
                    `,
                    }}
                    className="h-40 sm:h-32 flex gap-4 items-start bg-white border border-[#E5EDF5] rounded-2xl p-5 shadow-sm hover:shadow-md transition-all duration-300 max-w-xl"
                  >
                    {/* Icon */}
                    <div className="flex items-center justify-center">
                      <Icon className="w-10 h-10 text-[#10324F]" />
                    </div>

                    {/* Content */}
                    <div>
                      <h2 className="text-[#10324F] font-semibold text-sm">
                        {item.title}
                      </h2>

                      <p className="selfie-service-list-item-text mt-1">
                        {item.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      <section className="w-full bg-[#F6F9FB] py-10 px-6">
        <div className="max-w-6xl mx-auto mt-10 mb-10">
          {/* Heading */}
          <div className="text-center max-w-5xl mx-auto">
            <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F] uppercase">
              Create Branded HR Documents with Ease
            </h2>

            <p className="mt-6 text-sm leading-6 sm:text-base text-[#10324F] px-2 font-roboto  text-center max-w-3xl mx-auto">
              Branding Settings let you personalize HR and payroll documents
              with your company's identity. Add your logo, customize PDF
              templates, and apply brand colors to create professional offer
              letters, payslips, appointment letters, experience letters, and
              other employee documents with a consistent look
            </p>
          </div>

          {/* Cards */}
          <div className="mt-8 sm:mt-12 grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
            {brandingFeatures.map((item) => (
              <div
                key={item.id}
                style={{
                  boxShadow: `
                  rgb(167 191 210 / 30%) -15px -15px 20px inset,
                  rgb(170 188 202 / 20%) -20px -20px 30px
                `,
                }}
                className="bg-white rounded-xl shadow-sm border border-[#E4ECF3] p-4 sm:p-6 flex gap-3 items-start"
              >
                {/* Number Box */}
                <div className="min-w-8.5 h-8.5 rounded-md bg-[#16324F] font-roboto text-white flex items-center justify-center text-sm font-bold shadow">
                  {item.id}
                </div>

                {/* Content */}
                <div>
                  <h2 className="font-roboto text-sm font-semibold text-[#10324F] leading-5">
                    {item.title}
                  </h2>

                  <p className="mt-1 font-roboto text-xs  leading-5 text-[#10324F]">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Suspense fallback={null}>
        <section className="w-full bg-white py-14 px-6">
          <div className="max-w-6xl mx-auto mt-10 mb-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div>
                <h2 className="text-xl lg:text-3xl font-bold text-[#10324F] leading-tight">
                  Manage User Access with Role-Based Permissions
                </h2>

                <p className="mt-4 text-[#10324F] text-base leading-8 max-w-md">
                  Control who can access your Waggex account with role-based
                  user management. Add new users, assign permissions, update
                  access levels, and manage administrator or HR accounts from
                  one place, ensuring every team member only sees the
                  information relevant to their role.
                </p>
              </div>

              {/* Right Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {userfeatures.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      boxShadow: `
                      rgb(167 191 210 / 40%) -15px -15px 20px inset,
                      rgb(170 188 202 / 30%) -20px -20px 30px
                    `,
                    }}
                    className="group flex items-center gap-4 rounded-2xl bg-white border border-gray-100 p-5 shadow-md"
                  >
                    <span className="font-roboto text-[#10324F] mt-1">▶</span>

                    <p className="text-sm md:text-[15px] font-medium text-[#10324F] leading-6">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        <section className="w-full bg-[#F6F9FB] py-10 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-4 items-center">
              {/* Left Content */}
              <div className="lg:col-span-1">
                <h2 className="text-xl lg:text-3xl font-bold text-[#10324F] leading-tight">
                  Centralize Your Employee Referral Program
                </h2>

                <p className="mt-8 text-[#10324F] text-base leading-8">
                  Access referral settings, review referral history, and manage
                  employee referral records from one place. Keep referral
                  information organized, simplify administration, and maintain
                  better visibility across your company's referral activities
                </p>
              </div>

              {/* Center */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                <div
                  className="bg-white rounded-2xl border border-gray-100 px-8 py-8 min-w-65"
                  style={{
                    boxShadow: `
                      rgb(167 191 210 / 40%) -15px -15px 20px inset,
                      rgb(170 188 202 / 30%) -20px -20px 30px
                    `,
                  }}
                >
                  <h2 className="text-sm font-semibold text-[#10324F] leading-6">
                    Referral System Can
                    <br />
                    Help With
                  </h2>
                </div>
                <div className="ml-2">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    width="30"
                    height="40"
                    viewBox="0 0 52 44"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="rotate-90 md:rotate-0"
                  >
                    <defs>
                      <linearGradient
                        id="arrowGradient"
                        x1="0"
                        y1="0"
                        x2="1"
                        y2="1"
                      >
                        <stop offset="0%" stopColor="#B8C4D1" />
                        <stop offset="100%" stopColor="#6E8194" />
                      </linearGradient>
                    </defs>

                    <path
                      d="M0 10H28V0L52 22L28 44V34H0V10Z"
                      fill="url(#arrowGradient)"
                    />
                  </svg>
                </div>
              </div>

              {/* Right Cards */}
              <div className="space-y-4">
                {referralFeatures.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      boxShadow: `
                      rgb(167 191 210 / 40%) -15px -15px 20px inset,
                      rgb(170 188 202 / 30%) -20px -20px 30px
                    `,
                    }}
                    className="group flex items-center gap-4 rounded-2xl bg-white border border-gray-100 px-6 py-5 shadow-md"
                  >
                    <Check size={28} strokeWidth={2.5} />

                    <p className="text-[#10324F] font-medium text-sm md:text-[15px]">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        <section className="w-full bg-[#FFFFFF]  py-10  px-6 overflow-hidden">
          <div className="max-w-6xl mx-auto mt-4 mb-4">
            {/* Heading */}
            <div className="text-center">
              <h2 className="text-[#10324F] text-xl sm:text-3xl font-bold uppercase">
                Set Up Your Company in Minutes
              </h2>

              <p className="mt-6 font-roboto font-normal text-base leading-6 text-center text-[#10324F]">
                Most Indian businesses still rely on WhatsApp check-ins and
                manual registers. Here is how Waggex changes that.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-12">
              {steps.map((item) => (
                <div
                  key={item.id}
                  style={{
                    boxShadow: `
                    rgb(167 191 210 / 40%) -15px -15px 20px inset,
                    rgb(170 188 202 / 30%) -20px -20px 30px
                  `,
                  }}
                  className="rounded-2xl bg-white border border-gray-100 p-6 shadow-md"
                >
                  <span className="text-xs font-semibold text-[#10324F]">
                    {item.step}
                  </span>

                  <h2 className="mt-2 text-lg font-bold text-[#10324F]">
                    {item.title}
                  </h2>

                  <p className="mt-2 text-[#10324F]  text-xs leading-loose">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
        <section className="w-full bg-[#F6F9FB] py-10 px-6">
          <div className="max-w-6xl mx-auto">
            {/* Heading */}
            <div className="text-center max-w-5xl mx-auto">
              <h2 className="text-xl leading-7.5 sm:text-3xl font-roboto font-bold text-[#16324F] uppercase">
                Benefits of Waggex Profile Settings
              </h2>
            </div>

            {/* Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-14">
              {profileSettingBenefits.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.id}
                    style={{
                      boxShadow: `
                      rgb(167 191 210 / 40%) -15px -15px 20px inset,
                      rgb(170 188 202 / 30%) -20px -20px 30px
                    `,
                    }}
                    className="bg-white rounded-2xl shadow-[0_10px_35px_rgba(16,50,79,0.08)] border border-gray-100 p-5 flex gap-5 hover:-translate-y-1 hover:shadow-[0_15px_40px_rgba(16,50,79,0.15)] transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#10324F] flex items-center justify-center shadow-lg shrink-0">
                      <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                    </div>

                    <div>
                      <h2 className="text-sm font-semibold text-[#10324F] mb-2">
                        {item.title}
                      </h2>

                      <p className="text-[#10324F] leading-7 text-xs">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        <section className="py-10 px-6 bg-white">
          <div className="max-w-6xl mx-auto mt-1 mb-10">
            {/* Heading */}
            <div className="text-center max-w-4xl mx-auto">
              <h2 className="text-xl md:text-3xl font-bold text-[#10324F] uppercase">
                Connected with Waggex HRMS Platform
              </h2>

              <p className="mt-5 text-[#10324F] text-lg leading-8">
                Profile Settings supports your complete Waggex HRMS setup,
                including:
              </p>
            </div>

            {/* Modules */}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-20 max-w-4xl mx-auto p-8 sm:p-0">
              {hrmsModules.map((module, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 group cursor-pointer"
                >
                  {/* Icon */}
                  <span className="font-roboto text-[#10324F] font-bold">
                    ▶
                  </span>

                  {/* Text */}
                  <span className="text-lg font-medium text-[#10324F] group-hover:text-[#0B2740] transition">
                    {module}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
        <CustomerReviews />
        {/* <FAQ /> */}
        <HomeFaq
          faq1={Faqs1}
          faq2={Faqs2}
          subtitle="Common questions from Indian businesses evaluating Waggex GPS Attendance Software"
        />
        <PayrollBanner
          heading="Simplify Company Administration with Waggex"
          description="Manage company information, branding, users, and organization settings from one platform designed to save time and keep everything organized"
          buttonText="Try For Free"
        />
        <AppDownloadCard />
        <ModernizeHR
          heading="Simplify Payroll Processing With Waggex"
          description="Run accurate, compliant payroll every month using payroll management software built for the way Indian businesses actually operate. No spreadsheets. No errors."
        />
      </Suspense>
    </>
  );
}
